import { useCallback, useMemo } from "react";
import { DialogContent, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import type {
  ExtendedVisionUser,
  FormBuilderField,
} from "@thalamos/vision-common";
import {
  emailSchema,
  FieldBuilder,
  nameSchema,
  OrganisationRoleSchema,
  orgIdSchema,
  tk,
} from "@thalamos/vision-common";

import api from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import type { UserContextType } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { useOrganisations } from "../FormBuilder/OrganisationDropdown/OrganisationContext.js";
import { SinglePageForm } from "../FormBuilder/SinglePageForm/SinglePageForm.js";
import { TypographyI18N } from "../index.js";

const OnboardingFormDataSchema = z.object({
  name: new FieldBuilder().withSchema(nameSchema).build().schema,
  orgId: new FieldBuilder().withSchema(orgIdSchema).captureValueLabel().build()
    .schema,
  email: new FieldBuilder().withSchema(emailSchema).build().schema,
});

export type OnboardingFormData = z.infer<typeof OnboardingFormDataSchema>;
//          ^?

const collectUserDetails: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: FormBuilderField<OnboardingFormData, any>[];
} = {
  fields: [
    {
      type: "textbox",
      fieldName: "name",
      label: tk("onboarding.name"),
    },
    {
      type: "typography",
      fieldName: "email",
      label: tk("onboarding.email"),
      textFn: ({ formValues }) => [formValues.email.value],
    },
    {
      type: "organisation",
      fieldName: "orgId",
      label: tk("onboarding.organisationMembership"),
      filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
      // Include orgs marked as excludeAsIncidentLocation so that non-BCU staff
      // can select an appropriate home base during onboarding.
      includeExcludedAsIncidentLocation: true,
    },
  ],
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const content: { fields: FormBuilderField<OnboardingFormData, any>[] } =
  collectUserDetails;

const OnboardingForm = ({
  onSubmit,
  user,
}: {
  onSubmit: (values: OnboardingFormData) => Promise<void> | void;
  user: ExtendedVisionUser;
}) => {
  const { organisations } = useOrganisations();

  const initialOrgId = useMemo(() => {
    const sessionContext = user.sessionContext;

    // Only prefill when the user's session org is a command unit
    if (
      sessionContext?.organisationRole !==
      OrganisationRoleSchema.enum.PoliceCommandUnit
    ) {
      return { value: "", valueLabel: "" };
    }

    // Only prefill if that org is actually in the available dropdown options
    const availableCommandUnits = organisations.filter(
      (org) => org.role === OrganisationRoleSchema.enum.PoliceCommandUnit,
    );
    const matchingOrg = availableCommandUnits.find(
      (org) => org.id === sessionContext.organisationId,
    );

    if (!matchingOrg) {
      return { value: "", valueLabel: "" };
    }

    return {
      value: matchingOrg.id,
      valueLabel: matchingOrg.name,
    };
  }, [organisations, user.sessionContext]);

  return (
    <SinglePageForm<typeof OnboardingFormDataSchema>
      schema={OnboardingFormDataSchema}
      initialValues={{
        name: { value: user.name },
        email: { value: user.email },
        orgId: initialOrgId,
      }}
      content={content}
      onSubmit={onSubmit}
      ariaLabel="Onboarding"
    />
  );
};

export type OnboardingInnerProps = {
  user: ExtendedVisionUser;
  onSubmit: (values: OnboardingFormData) => Promise<void> | void;
};

export const OnboardingInner = ({ user, onSubmit }: OnboardingInnerProps) => {
  const theme = useTheme();
  return (
    <PopupDialog open={true} sx={{ height: "100%" }}>
      <DialogContent>
        <TypographyI18N
          translationKey={tk("onboarding.welcomeTitle")}
          sx={{
            paddingBottom: theme.spacing(2),
            fontWeight: 500,
          }}
        />
        <TypographyI18N
          translationKey={tk("onboarding.welcomeMessage")}
          sx={{
            paddingBottom: theme.spacing(4),
          }}
        />
        <OnboardingForm onSubmit={onSubmit} user={user} />
      </DialogContent>
    </PopupDialog>
  );
};

// This component is responsible for managing the API calls and navigation,
// passing the wired-up onSubmit handler to the inner component.
export const Onboarding = ({
  user,
  refetchUser,
}: {
  user: ExtendedVisionUser;
  refetchUser: UserContextType["refetchUser"];
}) => {
  const navigate = useNavigate();
  const notifications = useToastNotifications();
  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (values: OnboardingFormData) => {
      try {
        if (!user.sessionContext) {
          notifications.show(t(tk("onboarding.error")), {
            severity: "error",
          });
          return;
        }

        const response = await api.user.onboard(user.id, {
          name: values.name.value,
          currentOrganisationId: user.sessionContext.organisationId,
          organisationId: values.orgId.value,
          role: user.sessionContext.role,
        });
        if (response.status === 204) {
          await refetchUser();
          navigate(createRoute.home());
          return;
        } else {
          throw new Error(JSON.stringify(response.data.message));
        }
      } catch {
        notifications.show(t(tk("onboarding.error")), {
          severity: "error",
        });
      }
    },
    [navigate, notifications, refetchUser, t, user.id, user.sessionContext],
  );

  return <OnboardingInner user={user} onSubmit={onSubmit} />;
};
